import {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import * as detailsSelectors from '~/CreateFabricInvitation/reducers/details/selectors';
import {selectRsvpOff} from '~/CreateFabricInvitation/reducers/details/selectors';
import {HIDE_NOTIFY_GUESTS_OVERLAY} from '~/CreateFabricInvitation/reducers/modals/constants';
import {notifyGuestsOverlayShowing} from '~/CreateFabricInvitation/reducers/modals/selectors';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {getEventHostId, getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {getLoggedUserId} from '~/common/fabric/User/reducers/user';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import './NotifyGuestsOverlay.sass';

const MESSAGE = 'I’ve made an update. Check it out to see what’s new.';
const [ALL, ...GROUPS] = ['all', 'yes', 'no', 'undecided'];
const TO = [ALL];

export const NotifyGuestsOverlay = () => {
  const eventId = useSelector(getEventId);
  const hostId = useSelector(getEventHostId);
  const loggedUserId = useSelector(getLoggedUserId);
  const host = useSelector(detailsSelectors.selectHost);
  const coHost = useSelector(detailsSelectors.selectCohostName);
  const dispatch = useDispatch();
  const showModal = useSelector(notifyGuestsOverlayShowing);
  const rsvpOff = useSelector(selectRsvpOff);

  const [message, setMessage] = useState(MESSAGE);
  const [to, setTo] = useState(TO);

  const from = hostId === loggedUserId ? host : coHost;

  const closeOverlay = (confirmationType) => {
    dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {confirmationType},
    });
    dispatch({type: HIDE_NOTIFY_GUESTS_OVERLAY});
  };

  const handleSend = async () => {
    const url = `/tsunami/v1/services/event/${eventId}/broadcast`;
    try {
      await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          // biome-ignore lint/style/useNamingConvention: existing error
          q_message_type: 'guest_update_event',
          // biome-ignore lint/style/useNamingConvention: existing error
          virtual_groups: [...to].join('|'),
          message,
        }),
      });
    } catch (error) {
      evite.error(error);
    }
    closeOverlay('sent');
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const handleClick = useCallback(
    (group) => {
      if (group === ALL) {
        // if ALL is clicked, select all
        setTo([ALL]);
      } else {
        // grab everything but the ALL group since
        // we determined above they didn't select it
        let newTo = to.filter((t) => t !== ALL);

        // if group is currently selected
        if (newTo.includes(group)) {
          // deselect the group
          newTo = newTo.filter((t) => t !== group);
        } else {
          // otherwise append the group to the selected list
          newTo = [...newTo, group];
        }

        // if we have something selected, set it
        if (newTo.length > 0) {
          setTo(newTo);
        } else {
          setTo([ALL]);
        }
      }
    },
    [to, setTo]
  );

  if (!showModal) return null;

  return (
    <ResponsivePanel
      isOpen
      onClose={() => closeOverlay('updated')}
      title="Send an update to your guests"
      secondaryText="Skip"
      primaryText="Send"
      portalMode
      showCover
    >
      <div className="notify-guests-overlay">
        <Typography variant="label2" className="notify-guests-overlay__from-lbl">
          From:
        </Typography>
        <Typography variant="paragraph2" className="notify-guests-overlay__from">
          {from}
        </Typography>
        {!rsvpOff && (
          <>
            <Typography variant="label2" className="notify-guests-overlay__send-to-lbl">
              To:
            </Typography>
            <div className="notify-guests-overlay__send-to">
              {[ALL, ...GROUPS].map((group) => {
                const modifier = to.includes(group) ? '--active' : '';
                const className = `notify-guests-overlay__send-to-group${modifier}`;

                return (
                  <Typography
                    key={group}
                    variant="label2"
                    color="pebble"
                    className={className}
                    onClick={() => handleClick(group)}
                  >
                    {group}
                  </Typography>
                );
              })}
            </div>
          </>
        )}
        <hr style={{margin: '1.5rem 0'}} />
        <div className="notify-guests-overlay__message-lbl">
          <Typography variant="label2">Message</Typography>
          <Input
            data-qa-id="event-update-message-body"
            type="textarea"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="notify-guests-overlay__message-field"
          />
        </div>
        <div className="notify-guests-overlay__actions">
          <Button
            variant="secondary"
            size="large"
            className="notify-guests-overlay__actions-btn"
            onClick={() => closeOverlay('updated')}
            data-qa-id="notify-guests__skip"
          >
            Skip
          </Button>
          <Button
            variant="primary"
            size="large"
            className="notify-guests-overlay__actions-btn"
            onClick={handleSend}
            data-qa-id="notify-guests__send"
          >
            Send
          </Button>
        </div>
      </div>
    </ResponsivePanel>
  );
};
